import { DrillLog, HoleGeology, HoleGeologyRow } from 'src/app/services/hole.service';
import { GeologyInterval, GeologyKind, metresPerUnit } from 'src/app/components/blast3-dviewer/geology-model';

// ─────────────────────────────────────────────────────────────────────────────
// Geology helpers for the Holes › Geology view (pure functions, no Angular)
// ─────────────────────────────────────────────────────────────────────────────

/** Number of legend steps of the lithology ratio gradient, spread over 0 % → 100 %. */
export const LITHOLOGY_GRADIENT_STEPS = 4;

/** Used when the selected lithology has no colour in the geology definition list. */
const DEFAULT_LITHOLOGY_COLOR = '#727272';

export interface LithologyGradientStep {
  /** Band bounds as a percent of the hole length; the band is `]from; to]`. */
  from:  number;
  to:    number;
  color: string;
}

/**
 * The legend scale: four shades of `baseColor`, from the palest (]0; 25 %]) to the
 * colour itself (]75; 100 %]). `baseColor` is the lithology's own colour, as defined
 * in the site geology definition list. A hole with none of the lithology (0 %) is
 * off the scale and stays uncoloured — see `gradientStepFor`.
 */
export function lithologyGradient(baseColor: string | null | undefined): LithologyGradientStep[] {
  const [red, green, blue] = parseHexColor(baseColor) ?? parseHexColor(DEFAULT_LITHOLOGY_COLOR)!;
  const band = 100 / LITHOLOGY_GRADIENT_STEPS;

  return Array.from({ length: LITHOLOGY_GRADIENT_STEPS }, (_unused, index) => ({
    from:  index * band,
    to:    (index + 1) * band,
    color: tintOnWhite(red, green, blue, (index + 1) / LITHOLOGY_GRADIENT_STEPS),
  }));
}

/**
 * Index in `lithologyGradient` for a ratio in percent, or null when the ratio is off
 * the scale — null (no samples) or 0 % (the hole holds none of the lithology). Bands
 * are `]from; to]`, so 25 % lands on the first step and 25.1 % on the second.
 */
export function gradientStepFor(ratioPercent: number | null | undefined): number | null {
  if (ratioPercent == null || Number.isNaN(ratioPercent) || ratioPercent <= 0) return null;

  const index = Math.ceil(ratioPercent / (100 / LITHOLOGY_GRADIENT_STEPS)) - 1;
  return Math.min(LITHOLOGY_GRADIENT_STEPS - 1, Math.max(0, index));
}

/** Ids of every lithology sampled by at least one hole of the blast. */
export function lithologyIdsInBlast(rows: Iterable<HoleGeologyRow> | null | undefined): Set<number> {
  const ids = new Set<number>();
  for (const row of rows ?? []) {
    for (const sample of row.Geology?.lithologySamples ?? []) ids.add(sample.lithologyId);
  }
  return ids;
}

/**
 * Share of the hole (percent of its length) made of `lithologyId`.
 * Returns null when the hole has no lithology samples at all.
 */
export function lithologyRatioPercent(geology: HoleGeology | null | undefined, lithologyId: number): number | null {
  const samples = geology?.lithologySamples;
  if (!samples?.length) return null;

  const scale = percentScale(samples);
  let sum = 0;
  for (const sample of samples) {
    if (sample.lithologyId !== lithologyId) continue;
    sum += Math.max(0, sample.toDepthPercent - sample.fromDepthPercent) * scale;
  }
  return Math.min(100, Math.max(0, sum));
}

/** Lithology covering the largest share of the hole, or null without samples. */
export function dominantLithologyId(geology: HoleGeology | null | undefined): number | null {
  const samples = geology?.lithologySamples;
  if (!samples?.length) return null;

  const totals = new Map<number, number>();
  for (const sample of samples) {
    const span = Math.max(0, sample.toDepthPercent - sample.fromDepthPercent);
    totals.set(sample.lithologyId, (totals.get(sample.lithologyId) ?? 0) + span);
  }

  let best: number | null = null;
  let bestSpan = -1;
  for (const [id, span] of totals) {
    if (span > bestSpan) { best = id; bestSpan = span; }
  }
  return best;
}

export function hasDiscontinuity(drillLogs: readonly DrillLog[] | null | undefined, discontinuityId: number): boolean {
  return !!drillLogs?.some(log => log.discontinuityId === discontinuityId);
}

/** Ids of every discontinuity logged by at least one hole of the blast (0 = unset, dropped). */
export function discontinuityIdsInBlast(rows: Iterable<HoleGeologyRow> | null | undefined): Set<number> {
  const ids = new Set<number>();
  for (const row of rows ?? []) {
    for (const log of row.drillLogs ?? []) {
      if (log.discontinuityId) ids.add(log.discontinuityId);
    }
  }
  return ids;
}

/**
 * Slices of every hole for the 3D block model: lithology samples (percent of the hole) or drill
 * log entries (metres from the collar). Holes without any slice are left out.
 */
export function intervalsByHole(rows: ReadonlyMap<string, HoleGeologyRow>, kind: GeologyKind): Map<string, GeologyInterval[]> {
  const holes = new Map<string, GeologyInterval[]>();
  for (const [holeId, row] of rows) {
    const intervals = kind === 'lithology'
      ? (row.Geology?.lithologySamples ?? []).map(sample => ({ id: sample.lithologyId, from: sample.fromDepthPercent, to: sample.toDepthPercent }))
      : (row.drillLogs ?? []).filter(log => log.discontinuityId).map(log => ({ id: log.discontinuityId, from: log.fromDepth, to: log.toDepth }));
    if (intervals.length) holes.set(holeId, intervals);
  }
  return holes;
}

/**
 * Share (percent) of each lithology / drill log in the logged length of the whole blast, shown
 * next to each layer of the 3D legend. Slices are turned into metres and cut to the hole like the
 * 3D model does; a hole of unknown length (missing geometry, not in the design) is not counted,
 * whatever the kind, since the model cannot draw it either.
 */
export function intervalShares(
  holes: ReadonlyMap<string, readonly GeologyInterval[]>,
  kind: GeologyKind,
  holeLength: (holeId: string) => number,
): Map<number, number> {
  const metres = new Map<number, number>();
  let total = 0;
  for (const [holeId, intervals] of holes) {
    const length = holeLength(holeId);
    if (!(length > 0)) continue;

    const factor = metresPerUnit(kind, length);
    for (const interval of intervals) {
      const from = Math.min(length, Math.max(0, Math.min(interval.from, interval.to) * factor));
      const to = Math.min(length, Math.max(0, Math.max(interval.from, interval.to) * factor));
      const span = to - from;
      if (!(span > 0)) continue;
      metres.set(interval.id, (metres.get(interval.id) ?? 0) + span);
      total += span;
    }
  }
  for (const [id, length] of metres) metres.set(id, total > 0 ? (length / total) * 100 : 0);
  return metres;
}

export type SampledState = 'UNSET' | 'NOT_SAMPLED' | 'SAMPLED_ONCE' | 'SAMPLED_TWICE' | 'UNKNOWN';

/** Normalises the GraphQL enum (UPPER_SNAKE, PascalCase or proto snake_case) to one key. */
export function normalizeSampledState(raw: string | number | null | undefined): SampledState {
  if (raw === null || raw === undefined || raw === '') return 'UNSET';
  const key = String(raw).replace(/[^a-z0-9]/gi, '').toUpperCase();
  switch (key) {
    case 'UNSET':        case '0': return 'UNSET';
    case 'NOTSAMPLED':   case '1': return 'NOT_SAMPLED';
    case 'SAMPLEDONCE':  case 'SAMPLED': case '2': return 'SAMPLED_ONCE';
    case 'SAMPLEDTWICE': case 'DUPLICATED': case '3': return 'SAMPLED_TWICE';
    default: return 'UNKNOWN';
  }
}

/** Samples are documented as 0-100 percents; tolerate 0-1 fractions from older exports. */
function percentScale(samples: readonly { fromDepthPercent: number; toDepthPercent: number }[]): number {
  const max = Math.max(...samples.map(s => Math.max(s.fromDepthPercent, s.toDepthPercent)));
  return max > 0 && max <= 1 ? 100 : 1;
}

/** `#rgb` / `#rrggbb`, with or without the leading `#`; null when unparsable. */
function parseHexColor(color: string | null | undefined): [number, number, number] | null {
  const hex = color?.trim().replace(/^#/, '') ?? '';
  const full = hex.length === 3 ? hex.replace(/./g, char => char + char) : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** `weight` 0 → white, 1 → the colour itself. */
function tintOnWhite(red: number, green: number, blue: number, weight: number): string {
  const mix = (channel: number): string =>
    Math.round(255 + (channel - 255) * weight).toString(16).padStart(2, '0');

  return `#${mix(red)}${mix(green)}${mix(blue)}`;
}
